import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { camelCaseWeekShifts } from "../utils/departmentWeeks";
import type { WeekData } from "../utils/models";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

const NO_WEEKS: WeekData[] = [];
const noRefetch = (): void => undefined;

export type DepartmentDataWeeks = { weeks: WeekData[]; isLoading: boolean; isError: boolean; refetch: () => void };

/**
 * `department_data`'s own weeks, for the pages built on what only it carries: Planning (load, season and workforce
 * summaries) and Hiring (its weeks from the first one it returns). With `staffing_schedule_cache` off, the old list the
 * app loads at start, as before; on, the page asks for them when it opens, so the app never loads them at start.
 */
export const useDepartmentDataWeeks = (): DepartmentDataWeeks => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { hospitalData, chosenDepartment, timezone } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	// DEFAULT: today, unless the department pins a date, as the app asks department_data for.
	const wantDate = chosenDepartment.start_date?.toFormat("yyyy-MM-dd") ?? DateTime.now().setZone(timezone).toFormat("yyyy-MM-dd");
	const departmentData = useQuery<WeekData[]>({
		queryKey: ["department-data-weeks", departmentId, wantDate],
		queryFn: async () => camelCaseWeekShifts((await backendService.getDepartmentData(departmentId, wantDate, timezone)).data.weeks),
		enabled: isCacheEnabled,
	});
	if (!isCacheEnabled) return { weeks: hospitalData.weeks, isLoading: false, isError: false, refetch: noRefetch };
	// DEFAULT: no weeks while they load or after they fail; `isLoading` and `isError` say which.
	return { weeks: departmentData.data ?? NO_WEEKS, isLoading: departmentData.isLoading, isError: departmentData.isError, refetch: departmentData.refetch };
};
